import { QueryDataState } from '@shared/interfaces';
import { GIFT_SUPPORT_PHONE } from '@shared/misc/giftSupportPhone';

import { GiftChoiceUnitData } from '../giftChoiceUnit/giftChoiceUnit.interface';
import { GiftItemForStore } from '../giftItem/giftItem.interface';
import { Image } from '../media/media.interface';

// 공유 상태
export enum GiftCouponShareStatus {
  NotShared = 'NotShared', // 아직 공유 안 함
  Shared = 'Shared', // 공유함 (링크 생성)
  Viewed = 'Viewed', // 받는 사람이 조회함
}

/**
 * 공유 매체 (마지막으로 공유한 채널 / shareHistory에 함께 기록)
 * - KakaoTalk: 카카오톡 (웹훅으로도 자동 기록됨)
 * - SMS: 문자 메시지
 * - Link: 링크 복사 / Web Share API
 * - Other: 그 외
 */
export enum ShareChannel {
  KakaoTalk = 'KakaoTalk',
  SMS = 'SMS',
  Link = 'Link',
  Other = 'Other',
}

/**
 * 기프티콘 발송 매체 (API·관리자 UI·수신 안내 공통)
 * 선언 순서 = 매체 우선순위(표시 순서) — DELIVERY_CHANNEL_CONFIG 키 순서·백엔드 enum과 일치 유지
 */
export enum DeliveryChannel {
  Addcon = 'Addcon',
  Officecon = 'Officecon',
  GiftiShow = 'GiftiShow',
  EumGift = 'EumGift',
}

/**
 * 발송 알림 타입 (FE 표시 전용)
 * - SMS: 문자 — 기프티콘은 장문+이미지라 실제 발송은 항상 MMS지만 타입명만 SMS로 둔다
 */
export enum NotificationType {
  AlimTalk = 'AlimTalk',
  SMS = 'SMS',
}

export interface DeliveryChannelInfo {
  label: string;
  notificationType: NotificationType;
  /** 문자 발신번호 — 있을 때만 수신 안내에 발신번호 박스 노출 */
  senderNumber?: string;
  badgeClass: string;
}

export const DELIVERY_CHANNEL_CONFIG: Record<DeliveryChannel, DeliveryChannelInfo> = {
  [DeliveryChannel.Addcon]: {
    label: '애드콘',
    notificationType: NotificationType.SMS,
    senderNumber: GIFT_SUPPORT_PHONE,
    badgeClass: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  },
  [DeliveryChannel.Officecon]: {
    label: '오피스콘',
    notificationType: NotificationType.AlimTalk,
    badgeClass: 'border-blue-300 bg-blue-50 text-blue-700',
  },
  [DeliveryChannel.GiftiShow]: {
    label: '기프티쇼',
    notificationType: NotificationType.AlimTalk,
    badgeClass: 'border-red-300 bg-red-50 text-red-700',
  },
  [DeliveryChannel.EumGift]: {
    label: '1566-2373',
    notificationType: NotificationType.SMS,
    senderNumber: '1566-2373',
    badgeClass: 'border-purple-300 bg-purple-50 text-purple-700',
  },
};

/** `DELIVERY_CHANNEL_CONFIG` 키 순서 (추가 enum 값 누락 시 타입 오류) */
export const DELIVERY_CHANNEL_LIST = Object.keys(
  DELIVERY_CHANNEL_CONFIG,
) as DeliveryChannel[];

/** 관리자 발송 매체 `<select>` 표시문구 (config·notificationType만 사용) */
export function formatDeliveryChannelSelectLabel(channel: DeliveryChannel): string {
  const { label, notificationType } = DELIVERY_CHANNEL_CONFIG[channel];
  return notificationType === NotificationType.AlimTalk
    ? `${label} — 알림톡 (카카오톡)`
    : `${label} — 문자 (SMS)`;
}

// 교환 상태
export enum GiftCouponRedeemStatus {
  Available = 'Available', // 교환 가능
  Requested = 'Requested', // 교환 신청됨
  Delivered = 'Delivered', // 기프티콘 전달 완료
  Rejected = 'Rejected', // 발송 불가 (잘못된 번호 등)
  Expired = 'Expired', // 만료됨 (1년 경과)
  Refunded = 'Refunded', // 환불 완료
  Canceled = 'Canceled', // 주문 취소로 인한 폐기
  Disposed = 'Disposed', // 잘못된 전달로 인한 폐기
}

/**
 * 추가교환(중복 공유 쿠폰) 허용 정보 — 원본 쿠폰에 부여.
 * 남은건수 = allowed - used. expiresAt 미지정이면 무기한.
 * public 응답에는 { allowed, used, expiresAt? } 만 노출된다.
 */
export interface ExtraRedeemInfo {
  allowed: number;
  used: number;
  openedBy?: string;
  openedAt?: Date;
  reason?: string;
  expiresAt?: Date;
}

/** 추가결제 정보 — 스폰(추가교환으로 생성된) 쿠폰의 admin 전용 필드 */
export interface ExtraPaymentInfo {
  isPaid: boolean;
  method?: string;
  amount?: number;
  receiptId?: string;
  paidAt?: Date;
  paidBy?: string;
}

/**
 * 스폰 쿠폰 정산 방식.
 * - extraPayment: 추가결제로 충당 (매출 발생)
 * - inventory: 기구매 미전달 쿠폰(donor) 흡수로 충당 (추가 매출 없음, donor는 Disposed)
 */
export type GiftCouponSettledBy = 'extraPayment' | 'inventory';

// 쿠폰 기본 인터페이스
export interface GiftCoupon<
  TGiftOrder = string,
  TGiftItem = string,
  TSelectedChoiceUnit = string,
  TGifticonImage = string,
  TDeliveredBy = string,
  TRefundedBy = string,
> {
  _id: string;
  giftOrder: TGiftOrder;
  giftItem: TGiftItem;

  code: string; // 고유 코드 (6자리 hex)
  seq: number; // 주문 내 순번

  // 상태
  shareStatus: GiftCouponShareStatus;
  redeemStatus: GiftCouponRedeemStatus;

  sharedAt?: Date; // 공유한 시각
  viewedAt?: Date; // 받는 사람이 처음 본 시각
  confirmedAt?: Date; // 전달 확인한 시각
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /**
   * 가장 마지막 카카오 공유 웹훅의 hashChatId.
   * `duplicateRecipientCoupons[].hashChatId` 와 일치하는 항목이 있으면
   * "같은 사람에게 또 보냈을 가능성" 으로 본다.
   */
  lastSharedKakaoHashChatId?: string;
  /**
   * 같은 주문 안의 다른 쿠폰들 중 동일 hashChatId 로 카톡 공유된 기록.
   * `lastSharedKakaoHashChatId` 와 일치하는 항목만 실제 충돌로 판정한다
   * (다른 카톡방으로 재전달하면 자동 해소).
   */
  duplicateRecipientCoupons?: DuplicateRecipientCouponEntry[];
  /** 마지막으로 공유한 매체 (이미 Shared/Viewed 상태에서 channel만 들어와도 갱신) */
  lastSharedChannel?: ShareChannel;
  memo?: string;

  // 선택형 상품인 경우
  selectedChoiceUnit?: TSelectedChoiceUnit;

  // 교환 신청자 정보
  recipient?: {
    name: string;
    phoneNumber: string;
    requestedAt: Date;
    signatureImage?: Image;
  };

  // 관리자가 전달한 기프티콘
  gifticon?: {
    image: TGifticonImage;
    deliveredAt: Date;
    deliveredBy: TDeliveredBy;
    priceWhole: number;
    priceRetail: number;
    priceRebate: number;
    deliveryChannel: DeliveryChannel;
  };

  /**
   * 관리자 대리발송 기록 — 수령자 신청(번호인증·서명) 없이 관리자가 연락처를 받아
   * 직접 발송한 건. recipient.signatureImage 부재가 결함이 아니라 의도임을 명시하는
   * 감사 필드. (스펙: docs/gift-coupon-proxy-deliver-spec.md)
   */
  proxyDelivery?: {
    by?: string;
    at: Date;
  };

  /**
   * 정산 판매가 base (수수료·수익금·환불액의 기준) — 구매 당시 주문 스냅샷
   * (giftOrder.orderDetails[해당 giftItem].giftItemEmbed.priceRetail)을 쿠폰 생성 시 동결한 값.
   * 불변이라 정산·환불링크 등에서 giftOrder 조인 없이 이 값을 쓴다.
   * ※ gifticon.priceRetail(발송 스냅샷)은 선택정가(choiceUnit.priceRegular)가 박혀 판매가가
   *   아니므로 판매가가 필요하면 이 필드를 쓸 것.
   * (백엔드 2026-07-06 denormalize. 백필 전 레거시 문서엔 없을 수 있어 옵셔널 — 폴백 필요.)
   */
  salePriceRetail?: number;

  /**
   * 구매 시점 동결 수수료(원, 1원 올림 정수) — giftItemEmbed.feePerUnit denormalize
   * (2026-07-24, salePriceRetail과 동일 위상. 재발행·연장·스폰은 원본 상속).
   * 정산 수익금 = salePriceRetail − saleFeeCharge − 도매가 (저장값끼리 항등).
   * 레거시 문서엔 없음 — 서버가 정산 시점 요율 계산으로 폴백.
   */
  saleFeeCharge?: number;

  // 만료 정책
  purchasedAt: Date; // 구매일
  expiresAt: Date; // 구매일 + 1년

  // 환불 처리
  refund?: {
    amount?: number;
    bank?: string;
    accountNumber?: string;
    accountHolder?: string;
    phoneNumber?: string;
    signatureImage?: Image;
    requestedAt?: Date;
    refundedAt?: Date;
    refundedBy?: TRefundedBy;
  };

  // 환불/연장 신청 발행 (관리자가 고객에게 신청 링크를 허용)
  issued?: {
    type: 'refund' | 'renewal';
    issuedAt: Date;
    issuedBy: string;
    requestedAt?: Date;
  };

  shareHistory?: Array<{
    sharedAt: Date;
    unsharedAt?: Date;
    shareChannel?: ShareChannel;
  }>;

  // 재발행 쿠폰 여부
  isAdditional?: boolean;
  // 재발급 쿠폰 여부 (기존 쿠폰 폐기 후 재발급)
  isReissued?: boolean;
  // 연장쿠폰이 발급된 원본 쿠폰
  isRenewed?: boolean;
  // 이 쿠폰 자체가 연장쿠폰
  isRenewal?: boolean;
  // 연장쿠폰의 원본 쿠폰 참조
  originalCoupon?: string;
  // 연장 발급된 쿠폰 (issued 조회 시 populate)
  renewalCoupon?: GiftCoupon;
  // 폐기 사유 (redeemStatus === 'Disposed'일 때)
  disposalReason?: string;
  // 재발행 쿠폰 전달 시 입력한 수신자 전화번호
  additionalRecipientPhone?: string;

  // 번호 인증 기록
  verification?: {
    verificationId: string;
    phoneNumber: string;
    code: string;
    verifiedAt: Date;
  };

  // 추가교환(중복 공유 쿠폰) — 원본 쿠폰에 부여 (열렸을 때만 존재)
  extraRedeem?: ExtraRedeemInfo;
  // 추가교환으로 생성된 스폰 쿠폰 여부
  isExtraPaid?: boolean;
  // 스폰 쿠폰의 파생 원본 쿠폰 _id
  spawnedFrom?: string;
  // 추가결제 정보 (스폰 쿠폰 · admin 전용, public 미노출)
  extraPayment?: ExtraPaymentInfo;
  // 스폰 쿠폰 정산 방식 (정산 완료 시 세팅)
  settledBy?: GiftCouponSettledBy;
  // (B) 대체발송으로 흡수한 donor 쿠폰 _id (스폰 생존 + 상속)
  absorbedDonor?: string;

  // Virtual
  fullCode: string; // code-seq 형태
  /**
   * 카카오톡 공유 웹훅 역참조 (virtual). 백엔드가 populate 시 채워서 내려준다.
   * 같은 쿠폰이 여러 채팅방에 공유되었을 경우 콜백이 여러 건 누적된다.
   */
  kakaoShareCallbacks?: KakaoShareCallback[];

  createdAt: Date;
  updatedAt: Date;
}

// Store에서 사용하는 타입 (쿠폰 공유 페이지용)
export type GiftCouponForStore = GiftCoupon<
  string, // giftOrder ID
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

export type GiftCouponForStoreState = QueryDataState<GiftCouponForStore>;

export type GiftCouponListForStore = GiftCouponForStore[];

export type GiftCouponListForStoreState = QueryDataState<GiftCouponListForStore>;

// Public에서 사용하는 타입 (쿠폰 교환 페이지용)
export type GiftCouponForPublic = GiftCoupon<
  {
    _id: string;
    receiverName?: string;
  }, // giftOrder 일부 populate
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

export type GiftCouponForPublicState = QueryDataState<GiftCouponForPublic>;

// Admin 교환신청 목록에서 사용하는 타입 (giftOrder populate 포함)
export interface GiftCouponOrderInfo {
  _id: string;
  paymentDetail?: {
    purchasedAt?: Date;
  };
  bugo?: {
    _id: string;
    member?: {
      _id: string;
      fullName: string;
      phoneNumber: string;
    };
  };
}

export type GiftCouponForRedemption = GiftCoupon<
  GiftCouponOrderInfo, // giftOrder populate
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

/**
 * 같은 주문 내 다른 쿠폰 중 동일 카톡방(hashChatId) 으로 전달된 기록.
 * 백엔드가 카톡 공유 웹훅 수신 시 자동 갱신한다.
 *
 * UI 상 "같은 분께 또 보냄" 안내 노출 조건:
 *   `coupon.lastSharedKakaoHashChatId === entry.hashChatId`
 * 인 항목이 1건 이상 있어야 함. 단순히 배열 길이만 보면 안 됨
 * (다른 채팅방으로 재전달했을 때 자동 해소되는 핵심 로직).
 */
export interface DuplicateRecipientCouponEntry {
  hashChatId: string;
  /** 충돌난 쿠폰의 ObjectId */
  coupon: string;
  /** 충돌난 쿠폰의 code */
  code: string;
  /** 충돌난 쿠폰의 순번(#N번째) */
  seq: number;
}

// Admin 주문목록에서 사용하는 간소화 쿠폰 타입 (virtual populate select)
export interface GiftCouponSummary {
  _id: string;
  giftItem: string;
  code: string;
  seq: number;
  shareStatus: GiftCouponShareStatus;
  redeemStatus: GiftCouponRedeemStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  confirmedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 가장 마지막 카카오 공유 웹훅의 hashChatId (다건수신 판정용) */
  lastSharedKakaoHashChatId?: string;
  /** 같은 주문 내 동일 카톡방으로 전달된 다른 쿠폰들 (다건수신 판정용) */
  duplicateRecipientCoupons?: DuplicateRecipientCouponEntry[];
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: ShareChannel;
  memo?: string;
  selectedChoiceUnit?: GiftChoiceUnitData | string;
  recipient?: {
    name: string;
    phoneNumber: string;
    requestedAt: Date;
    signatureImage: Image;
  };
  gifticon?: {
    deliveredAt?: Date;
  };
  /** 관리자 대리발송(신청 생략) 기록 — 있으면 "대리발송" 배지 표시 */
  proxyDelivery?: {
    by?: string;
    at: Date;
  };
  /**
   * 정산 판매가 base (수수료·수익금·환불액 기준) — 구매 당시 주문 스냅샷을 쿠폰 생성 시 동결한 값.
   * gifticon 발송 스냅샷(선택정가)이 아니라 판매가가 필요하면 이 필드를 쓸 것.
   * (백엔드 denormalize. 백필 전 레거시엔 없을 수 있어 옵셔널 — 폴백 필요.)
   */
  salePriceRetail?: number;
  purchasedAt: Date;
  expiresAt: Date;
  refund?: {
    amount?: number;
    bank?: string;
    accountNumber?: string;
    accountHolder?: string;
    phoneNumber?: string;
    refundedAt?: Date;
    requestedAt?: Date;
  };
  issued?: {
    type: 'refund' | 'renewal';
    issuedAt: Date;
    issuedBy: string;
    requestedAt?: Date;
  };
  shareHistory?: Array<{
    sharedAt: Date;
    unsharedAt?: Date;
    shareChannel?: ShareChannel;
  }>;
  isAdditional?: boolean;
  isReissued?: boolean;
  isRenewed?: boolean;
  isRenewal?: boolean;
  originalCoupon?: string;
  disposalReason?: string;
  additionalRecipientPhone?: string;
  renewalRecipientPhone?: string;
  // 추가교환(중복 공유 쿠폰) 관련 — 백엔드 populate 시 포함
  extraRedeem?: ExtraRedeemInfo;
  isExtraPaid?: boolean;
  spawnedFrom?: string;
  extraPayment?: ExtraPaymentInfo;
  settledBy?: GiftCouponSettledBy;
  absorbedDonor?: string;
  /**
   * 카카오톡 공유 웹훅 역참조 (virtual). 백엔드가 populate 시 채워서 내려준다.
   * 같은 쿠폰이 여러 채팅방에 공유되었을 경우 콜백이 여러 건 누적된다.
   */
  kakaoShareCallbacks?: KakaoShareCallback[];
}

// 교환 신청 DTO
export interface GiftCouponRequestDto {
  name: string; // 교환 신청자 이름
  phoneNumber: string; // 교환 신청자 전화번호
  signatureImage?: string; // Base64 Data URL (data:image/png;base64,...)
  selectedChoiceUnit?: string; // 선택형 상품인 경우 선택한 ChoiceUnit ID
}

// 나중에 신청하기 — 수신자 번호만 미리 저장
export interface GiftCouponSaveRecipientDto {
  phoneNumber: string;
  selectedChoiceUnit?: string;
}

// 블랙리스트 항목 (중복 의심 번호)
export interface BlacklistItem {
  phoneNumber: string;
  count: number;
  names: string[];
}

// 쿠폰 통계 (Admin statistics)
export interface GiftCouponStatistics {
  pending: number;
  delivered: number;
  rejected: number;
  canceled: number;
  disposed: number;
  total: number;
  blacklist: BlacklistItem[];
}

// 과거 교환 이력 항목
export interface PhoneHistoryItem {
  redeemStatus: GiftCouponRedeemStatus;
  requestedAt: Date;
  ordererName: string;
  giftItemName?: string;
  selectedChoiceUnit?: {
    name: string;
    image?: Image | string;
  };
}

// 주문 단위 쿠폰 요약 (발송대기 대시보드 API 응답)
export interface OrderCouponSummary {
  purchasedCount: number;
  totalCouponCount: number;
  additionalCount: number;
  deliveredCount: number;
  requestedCount: number;
}

// 발송대기 대시보드용 쿠폰 (isDuplicatePhone, phoneHistory 포함)
export type GiftCouponForRedemptionDashboard = GiftCouponForRedemption & {
  isDuplicatePhone?: boolean;
  isAdditionalPhoneConflict?: boolean;
  phoneHistory?: PhoneHistoryItem[];
  redeemSequence?: number;
  orderCouponSummary?: OrderCouponSummary;
};

// 발송대기 대시보드 응답
export interface RedemptionDashboardData {
  requested: GiftCouponForRedemptionDashboard[];
  todayDelivered: GiftCouponForRedemption[];
}

/**
 * 발송대기 — 매체별 충전 필요액 예측 (admin).
 * 스펙: docs/gift-coupon-pending-charge-estimate-spec.md
 * 구서버에는 엔드포인트가 없어 404가 날 수 있다 — 호출부에서 가드하고 영역을 숨긴다.
 */
export type PendingChargeEstimateChannel = DeliveryChannel | null; // null = 미분류

export interface PendingChargeEstimateItem {
  key: string; // "unit:<id>" | "info:<id>" | "item:<id>"
  name: string;
  count: number;
  unitPriceWhole: number;
  /** recentDelivered = 같은 상품 최근 발송가(신뢰도 최고), 그 외는 카탈로그 폴백 */
  priceSource: 'recentDelivered' | 'choiceUnit' | 'giftItem' | 'none';
  channelSource: 'recentDelivered' | 'wholeUrl' | 'none';
  /** priceSource=recentDelivered일 때 참조한 발송 시각 — 가격 신선도 표시용 */
  refDeliveredAt?: Date;
}

export interface PendingChargeEstimateChannelBucket {
  channel: PendingChargeEstimateChannel;
  pendingCount: number;
  estimatedAmount: number;
  items: PendingChargeEstimateItem[];
}

export interface PendingChargeEstimateResponse {
  generatedAt: Date;
  /** 발송대기 탭 (N)과 동일 기준 (Requested − 미정산 추가결제 대기) */
  totalPendingCount: number;
  totalEstimatedAmount: number;
  /** estimatedAmount 내림차순, 미분류(null)는 항상 마지막 */
  channels: PendingChargeEstimateChannelBucket[];
}

// 쿠폰 검색 파라미터
export type DateField = 'requestedAt' | 'deliveredAt';

export interface GiftCouponSearchParams {
  phoneNumber?: string;
  ordererName?: string;
  ordererPhone?: string;
  fullCode?: string;
  dateFrom?: string;
  dateTo?: string;
  dateField?: DateField;
  redeemStatus?: GiftCouponRedeemStatus | GiftCouponRedeemStatus[];
  shareStatus?: GiftCouponShareStatus;
}

// 코드로 쿠폰 조회 시 사용하는 타입
export type GiftCouponByCode = GiftCouponForPublic;

export type GiftCouponByCodeState = QueryDataState<GiftCouponByCode>;

// 카카오톡 공유 웹훅 콜백
// args.shareContent === 'GiftCoupon' | 'GiftDelivery' 로 구분되며
// 각각 args.couponId / args.deliveryId, args.couponCode, args.domainUrl 를 포함합니다.
export interface KakaoShareCallback {
  _id: string;
  chatType: string;
  hashChatId: string;
  templateId: number;
  args: {
    shareContent?: 'GiftCoupon' | 'GiftDelivery';
    couponId?: string;
    deliveryId?: string;
    couponCode?: string;
    domainUrl?: string;
    [key: string]: string | undefined;
  };
  /** 백엔드에서 args.couponId 를 별도 필드로 노출 (조건부) */
  couponId?: string;
  /** 백엔드에서 args.deliveryId 를 별도 필드로 노출 (조건부) */
  deliveryId?: string;
  appName?: string;
  kakaoResourceId?: string;
  createdAt: string;
  updatedAt: string;
}

// 번호 인증 관련 DTO / 응답
export interface VerificationSendDto {
  phoneNumber: string;
  giftCouponId: string;
  reason?: string;
  /**
   * 추가교환(extra) 신청용 인증 여부.
   * true면 백엔드는 이 인증을 원본 쿠폰(verification)에 덮어쓰지 않고 별도로 보관했다가,
   * 추가신청/나중에받기로 spawn 쿠폰 생성 시 그 spawn에 연결한다. (원본 데이터 보존)
   */
  isExtra?: boolean;
}

export interface VerificationSendResponse {
  verificationId: string;
  expiresAt: string;
}

export interface VerificationConfirmDto {
  verificationId: string;
  code: string;
  giftCouponId: string;
  phoneNumber: string;
  /** 추가교환(extra) 신청용 인증 여부 — send와 동일하게 원본 보존용 */
  isExtra?: boolean;
}

export interface VerificationConfirmResponse {
  verified: boolean;
}
